import React, { useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { checkHasData } from "./utils.js";
import NoDataPanel from "./NoDataPanel";
import ErrorPanel from "./ErrorPanel";
import ErrorPanel404 from "./ErrorPanel404";
import PanelPlaceholder from "./PanelPlaceholder";
import { useFetchPanelData } from "../../features/dashboard/usePanelData.js";
import { useFetchFilteredPanelData } from "../../features/dashboard/useFilteredPanelData.js";
import { selectApplyFiltersVersion } from "../../features/dashboard/dashboardSlice";
import { buildPanelRequestKey } from "../../features/dashboard/panelRequestUtils";
import PanelDescription from "./PanelDescription";
import PanelDescriptionInline from "./PanelDescriptionInline";

/**
 * Shared wrapper for panels. Handles data fetching, loading, error, and
 * empty-state rendering. Passes panelData and other utilities to the children render function.
 *
 * Usage:
 *   <PanelWrapper panel={panel}>
 *     {(panelData, hasData) => <MyPanel data={panelData} />}
 *   </PanelWrapper>
 *
 * Options:
 *   - showDescription: Include PanelDescription modal and inline description (default: false)
 *   - showNoDataPanel: Show NoDataPanel when no data (default: true)
 *   - customNoDataRender: Custom render function when no data (overrides showNoDataPanel)
 */
const PanelWrapper = ({ 
    panel, 
    children, 
    showDescription = false,
    showNoDataPanel = true,
    customNoDataRender = null
}) => {
    const modalId = `modalInfo#${panel.id}-${panel.chart_type || panel.type}`;
    const infoModal = showDescription ? <PanelDescription chart={panel} modalId={modalId} /> : null;
    const { panelObject: unfilteredPanelObject, fetchPanelData } = useFetchPanelData(panel.dashboard, panel);
    const { panelObject: filteredPanelObject, fetchFilteredPanelData } = useFetchFilteredPanelData(panel.dashboard, panel);
    const applyFiltersVersion = useSelector(selectApplyFiltersVersion);
    const panelObject = filteredPanelObject || unfilteredPanelObject;
    const panelData = panelObject?.data;
    const panelError = panelObject?.error;
    const panelLoading = panelObject?.loading;
    const httpStatusCode = panelObject?.httpStatusCode;
    const lastAppliedVersionRef = useRef(0);
    const lastInitialRequestKeyRef = useRef(null);
    const fetchPanelDataRef = useRef(fetchPanelData);
    const initialRequestKey = buildPanelRequestKey({
        dashboardId: panel.dashboard,
        panel,
    });

    useEffect(() => {
        fetchPanelDataRef.current = fetchPanelData;
    }, [fetchPanelData]);

    useEffect(() => {
        if (lastInitialRequestKeyRef.current === initialRequestKey) return;
        lastInitialRequestKeyRef.current = initialRequestKey;
        fetchPanelDataRef.current();
    }, [initialRequestKey]);

    useEffect(() => {
        if (applyFiltersVersion > 0 && lastAppliedVersionRef.current !== applyFiltersVersion) {
            lastAppliedVersionRef.current = applyFiltersVersion;
            fetchFilteredPanelData();
        }
    }, [applyFiltersVersion, fetchFilteredPanelData]);

    if (panelError) {
        return httpStatusCode === 404 ? (
            <ErrorPanel404
                panel={panel}
                reloadPanel={fetchFilteredPanelData}
                loading={panelLoading}
            />
        ) : (
            <ErrorPanel
                panel={panel}
                reloadPanel={fetchFilteredPanelData}
                loading={panelLoading}
            />
        );
    }

    if (panelLoading || !panelData) {
        return <PanelPlaceholder panel={panel} />;
    }

    const hasData = checkHasData(panelData);

    if (!hasData) {
        if (customNoDataRender) {
            return customNoDataRender();
        }
        if (showNoDataPanel) {
            return <NoDataPanel panel={{...panel, description: ""}} />;
        }
    }

    return (
        <div>
            {children(panelData, hasData)}
            {showDescription && <PanelDescriptionInline chart={panel} modalId={modalId} />}
            {infoModal}
        </div>
    );
};

export default PanelWrapper;
